/* src/sections/auth/festival/themes/GaneshChaturthiTheme.js */
import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------
// Divine Ganesh Chaturthi Color Palette - Warm & Festive
const COLORS = {
    // Sky Gradients (Warm Morning/Evening)
    SKY_TOP: '#FFB74D',             // Soft Orange
    SKY_MID: '#FF9800',             // Vibrant Orange
    SKY_BOTTOM: '#E65100',          // Deep Orange/Rust
    SKY_HORIZON: '#FFD54F',         // Golden Horizon

    // Modak & Flower Colors
    MODAK_WHITE: '#F5F5F5',         // Cream/White
    MODAK_SHADOW: '#E0E0E0',
    FLOWER_RED: '#D32F2F',          // Hibiscus Red
    FLOWER_PINK: '#F48FB1',         // Lotus Pink
    FLOWER_ORANGE: '#FF6F00',       // Marigold
    FLOWER_YELLOW: '#FFEB3B',       // Bright Yellow

    // Accent Colors
    IVORY: '#FFF9C4',
    GOLD: '#FFD700',
    DEEP_RED: '#B71C1C',            // Vermilion

    // Special Effects
    SPARKLE_GOLD: '#FFE082',
    GLOW_ORANGE: '#FFAB40',
};

// Ganesh Chaturthi Canvas Animation Engine
function GaneshCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext('2d');
        let animationFrameId;
        const particles = []; // Modaks and Flowers
        const clouds = [];
        const confetti = [];
        let textParticles = [];
        let time = 0;
        let sunPosition = { x: 0, y: 0, radius: 80 };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            sunPosition = {
                x: canvas.width * 0.5,
                y: canvas.height * 0.9, // Setting sun or rising sun at bottom center
                radius: canvas.width < 768 ? 100 : 150
            };
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // --- 1. Particle Factory (Modaks & Flowers) ---
        const particleTypes = ['modak', 'flower_red', 'flower_pink', 'flower_orange'];

        const createParticle = () => {
            const type = particleTypes[Math.floor(Math.random() * particleTypes.length)];

            return {
                x: Math.random() * canvas.width,
                y: canvas.height + Math.random() * 200, // Start below screen
                size: Math.random() * 25 + 15,
                type,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.02,
                swayOffset: Math.random() * Math.PI * 2,
                swaySpeed: Math.random() * 0.01 + 0.005,
                floatSpeed: Math.random() * 0.8 + 0.4,
                alpha: Math.random() * 0.3 + 0.7,
            };
        };

        const updateParticle = (p) => {
            p.swayOffset += p.swaySpeed;
            p.rotation += p.rotationSpeed;

            // Floating up motion
            p.y -= p.floatSpeed;
            p.x += Math.sin(p.swayOffset) * 0.8;

            // Reset if goes off top
            if (p.y < -50) {
                p.y = canvas.height + 50;
                p.x = Math.random() * canvas.width;
            }
        };

        const drawModak = (x, y, size, rotation, alphaVal) => {
            ctx.save();
            ctx.globalAlpha = alphaVal;
            ctx.translate(x, y);
            ctx.rotate(rotation);

            // Modak Shape
            ctx.fillStyle = COLORS.MODAK_WHITE;
            ctx.shadowBlur = 10;
            ctx.shadowColor = 'rgba(0,0,0,0.1)';

            ctx.beginPath();
            ctx.moveTo(0, -size);
            ctx.quadraticCurveTo(size, size * 0.5, 0, size);
            ctx.quadraticCurveTo(-size, size * 0.5, 0, -size);
            ctx.fill();

            // Lines for details
            ctx.strokeStyle = COLORS.MODAK_SHADOW;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, -size);
            ctx.lineTo(0, size);
            ctx.stroke();

            ctx.restore();
        };

        const drawFlower = (x, y, size, rotation, color, alphaVal) => {
            ctx.save();
            ctx.globalAlpha = alphaVal;
            ctx.translate(x, y);
            ctx.rotate(rotation);

            ctx.fillStyle = color;
            ctx.shadowBlur = 5;
            ctx.shadowColor = 'rgba(0,0,0,0.2)';

            // 5 Petals
            for (let i = 0; i < 5; i += 1) {
                ctx.rotate((Math.PI * 2) / 5);
                ctx.beginPath();
                ctx.ellipse(0, size * 0.6, size * 0.4, size, 0, 0, Math.PI * 2);
                ctx.fill();
            }

            // Center
            ctx.fillStyle = COLORS.GOLD;
            ctx.beginPath();
            ctx.arc(0, 0, size * 0.3, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        const drawParticle = (p) => {
            if (p.type === 'modak') {
                drawModak(p.x, p.y, p.size, p.rotation, p.alpha);
            } else if (p.type === 'flower_red') {
                drawFlower(p.x, p.y, p.size, p.rotation, COLORS.FLOWER_RED, p.alpha);
            } else if (p.type === 'flower_pink') {
                drawFlower(p.x, p.y, p.size, p.rotation, COLORS.FLOWER_PINK, p.alpha);
            } else {
                drawFlower(p.x, p.y, p.size, p.rotation, COLORS.FLOWER_ORANGE, p.alpha);
            }
        };

        // --- 2. Cloud Factory ---
        const createCloud = () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height * 0.3,
            size: Math.random() * 40 + 30,
            speed: Math.random() * 0.2 + 0.05,
            alpha: Math.random() * 0.3 + 0.2, // More subtle
            puffCount: Math.floor(Math.random() * 2) + 3,
        });

        const updateCloud = (cloud) => {
            cloud.x += cloud.speed;
            if (cloud.x > canvas.width + 100) {
                cloud.x = -100;
                cloud.y = Math.random() * canvas.height * 0.3;
            }
        };

        const drawCloud = (cloud) => {
            ctx.save();
            ctx.globalAlpha = cloud.alpha;
            ctx.fillStyle = COLORS.IVORY; // Warmer clouds
            ctx.shadowBlur = 15;
            ctx.shadowColor = COLORS.GOLD;

            for (let i = 0; i < cloud.puffCount; i += 1) {
                const offsetX = (i - cloud.puffCount / 2) * cloud.size * 0.5;
                const puffSize = cloud.size * (i === Math.floor(cloud.puffCount / 2) ? 1 : 0.7);

                ctx.beginPath();
                ctx.arc(cloud.x + offsetX, cloud.y, puffSize, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.restore();
        };

        // --- 3. Confetti (Marigold Petals) ---
        const createConfetti = () => ({
            x: Math.random() * canvas.width,
            y: -20,
            size: Math.random() * 4 + 2,
            color: Math.random() > 0.5 ? COLORS.FLOWER_ORANGE : COLORS.FLOWER_YELLOW,
            speedY: Math.random() * 1.5 + 0.5,
            speedX: (Math.random() - 0.5) * 1,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.2,
            alpha: Math.random() * 0.8 + 0.2,
        });

        const updateConfetti = (conf) => {
            conf.y += conf.speedY;
            conf.x += conf.speedX;
            conf.rotation += conf.rotationSpeed;

            if (conf.y > canvas.height + 20) {
                Object.assign(conf, createConfetti());
            }
        };

        const drawConfetti = (conf) => {
            ctx.save();
            ctx.globalAlpha = conf.alpha;
            ctx.translate(conf.x, conf.y);
            ctx.rotate(conf.rotation);

            ctx.fillStyle = conf.color;
            // Petal shape
            ctx.beginPath();
            ctx.ellipse(0, 0, conf.size, conf.size * 0.6, 0, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        // --- 4. Animated Text ---
        const createTextParticle = (x, y, char, index) => ({
            x,
            y: y - 100, // Start higher
            targetY: y,
            char,
            delay: index * 12, // Faster stagger
            time: 0,
            alpha: 0,
            scale: 0,
            bounce: 0,
        });

        const updateTextParticle = (textChar) => {
            textChar.time += 1;
            if (textChar.time > textChar.delay) {
                const progress = Math.min(1, (textChar.time - textChar.delay) / 25); // Faster animation
                const easeOut = 1 - (1 - progress) ** 3;

                textChar.y = textChar.targetY - (1 - easeOut) * 100;
                textChar.alpha = Math.min(1, progress * 2);
                textChar.scale = Math.min(1, progress * 1.2);

                if (progress >= 1) {
                    textChar.bounce = Math.sin(time * 0.05) * 3;
                }
            }
        };

        const drawTextParticle = (textChar) => {
            if (textChar.time < textChar.delay) return;

            ctx.save();
            ctx.globalAlpha = textChar.alpha;
            ctx.translate(textChar.x, textChar.y + textChar.bounce);
            ctx.scale(textChar.scale, textChar.scale);

            const fontSize = canvas.width < 768 ? 32 : 52;
            ctx.font = `bold ${fontSize}px "Comic Sans MS", cursive, sans-serif`; // Or a more traditional font if available
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Divine gradient text
            const gradient = ctx.createLinearGradient(0, -fontSize / 2, 0, fontSize / 2);
            gradient.addColorStop(0, COLORS.DEEP_RED);
            gradient.addColorStop(0.5, COLORS.FLOWER_ORANGE);
            gradient.addColorStop(1, COLORS.GOLD);

            // Glow effect
            ctx.shadowBlur = 25;
            ctx.shadowColor = COLORS.GLOW_ORANGE;

            ctx.fillStyle = gradient;
            ctx.fillText(textChar.char, 0, 0);

            // Outline
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2;
            ctx.strokeText(textChar.char, 0, 0);

            ctx.restore();
        };

        // --- 5. Sun Glow (Halo) ---
        const drawSun = () => {
            ctx.save();

            // Halo Glow
            const sunGradient = ctx.createRadialGradient(
                sunPosition.x, sunPosition.y, 0,
                sunPosition.x, sunPosition.y, sunPosition.radius
            );
            sunGradient.addColorStop(0, alpha(COLORS.GOLD, 0.4));
            sunGradient.addColorStop(0.5, alpha(COLORS.SKY_MID, 0.2));
            sunGradient.addColorStop(1, alpha(COLORS.SKY_BOTTOM, 0));

            ctx.fillStyle = sunGradient;
            ctx.beginPath();
            ctx.arc(sunPosition.x, sunPosition.y, sunPosition.radius, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        // --- Initialize Elements ---

        // Create Modaks & Flowers
        for (let i = 0; i < 20; i += 1) {
            particles.push(createParticle());
        }

        // Create clouds
        for (let i = 0; i < 6; i += 1) {
            clouds.push(createCloud());
        }

        // Create confetti petals
        for (let i = 0; i < 60; i += 1) {
            confetti.push(createConfetti());
        }

        // Create text
        const initText = () => {
            const text = 'GANPATI BAPPA MORYA!';
            const isDesktop = canvas.width >= 900;
            const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
            const centerY = canvas.height * 0.15;
            const charSpacing = canvas.width < 768 ? 20 : 35;
            const startX = centerX - (text.length * charSpacing) / 2;

            textParticles = text.split('').map((char, i) =>
                createTextParticle(startX + i * charSpacing, centerY, char, i)
            );
        };

        // Ultra-fast start
        setTimeout(initText, 100);

        // --- Animation Loop ---
        const animate = () => {
            time += 1;

            // Clear with warm gradient
            const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            bgGradient.addColorStop(0, COLORS.SKY_TOP);
            bgGradient.addColorStop(0.5, COLORS.SKY_MID);
            bgGradient.addColorStop(1, COLORS.SKY_BOTTOM);
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw sun/halo
            drawSun();

            // Draw clouds
            clouds.forEach(cloud => {
                updateCloud(cloud);
                drawCloud(cloud);
            });

            // Draw particles (Modaks/Flowers)
            particles.forEach(p => {
                updateParticle(p);
                drawParticle(p);
            });

            // Draw confetti
            confetti.forEach(conf => {
                updateConfetti(conf);
                drawConfetti(conf);
            });

            // Draw text
            textParticles.forEach(textChar => {
                updateTextParticle(textChar);
                drawTextParticle(textChar);
            });

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        // Handle resize
        const handleResize = () => {
            resizeCanvas();
            if (textParticles.length > 0) initText();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
            }}
        />
    );
}

// Main Ganesh Chaturthi Theme Component
export default function GaneshChaturthiTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            overflow: 'hidden',
        }}>
            <GaneshCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(20,8,0,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha(COLORS.GOLD, 0.5)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.4)}`,
                    `0 16px 64px ${alpha(COLORS.FLOWER_ORANGE, 0.25)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: `linear-gradient(90deg, transparent, ${COLORS.DEEP_RED}, ${COLORS.GOLD}, ${COLORS.FLOWER_ORANGE}, transparent)`,
                    borderRadius: '0 0 4px 4px',
                    animation: 'ganeshGlow 3s ease-in-out infinite',
                },
                '@keyframes ganeshGlow': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: `linear-gradient(90deg, ${COLORS.DEEP_RED}, ${COLORS.GOLD}, ${COLORS.FLOWER_ORANGE}, ${COLORS.DEEP_RED})`,
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha(COLORS.FLOWER_ORANGE, 0.5)}`,
                    }}>🐘 &nbsp; Ganpati Bappa Morya &nbsp; 🐘</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 2 }}>
                    <Logo />
                </Stack>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {cloneElement(children, { hideHelpLink: true })}
                </Box>
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha(COLORS.FLOWER_ORANGE, 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#3A1800', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

GaneshChaturthiTheme.propTypes = {
    children: PropTypes.node,
};
